import { ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { AutoBatchResponse, AutoCandidate } from "@opus-dt/shared-types";
import { objectiveAxis } from "../objective/objective-meta";
import { objDeltaValue, oeeDeltaValue } from "./metrics";
import { CandidateRankingFailedRow, CandidateRankingRow } from "./CandidateRankingRow";
import { CandidateRankingTable } from "./CandidateRankingTable";
import { RankingLegend, RankingOeeNote } from "./CandidateRankingNotes";
import { RANKING_GRID, RANKING_GRID_SINGLE } from "./ranking-layout";
import { deviationDomain } from "./charts/chart-scale";

interface CandidateRankingProps {
  batch: AutoBatchResponse;
  baseline: AutoCandidate | null;
  onOpen: (simulationId: string) => void;
}

// Ranking navegável. Cada linha tem DUAS faixas de desvio, com eixos
// separados: a do objetivo e a de OEE. Separadas porque não são a mesma grandeza (juntá-las num
// eixo só seria mentira), e ambas porque é justamente onde elas discordam que a decisão fica
// interessante — o candidato de maior OEE pode não ser o que entrega mais cedo.
// A base NÃO é mais uma linha da lista: ela é o próprio eixo. Repetida como linha, ela disputava
// a leitura com os candidatos e ainda assim não tinha faixa para mostrar.
export function CandidateRanking({ batch, baseline, onOpen }: CandidateRankingProps) {
  const ranked = batch.candidates
    .filter((c) => !c.isBaseline && c.status === "COMPLETED")
    .sort((a, b) => (a.rankPosition ?? 99) - (b.rankPosition ?? 99));
  const failed = batch.candidates.filter((c) => !c.isBaseline && c.status === "FAILED");

  const axis = objectiveAxis(batch.objective);
  // Objetivo que JÁ É o OEE: a segunda faixa repetiria a primeira, número por número.
  const showOee = batch.objective !== "OEE_IMPROVEMENT";
  const grid = showOee ? RANKING_GRID : RANKING_GRID_SINGLE;

  // Os eixos são COMPARTILHADOS por todas as linhas: uma escala por linha faria desvios
  // diferentes desenharem barras do mesmo tamanho.
  const objDomain = deviationDomain(ranked.map((c) => objDeltaValue(c, baseline, batch) ?? 0));
  const oeeDomain = deviationDomain(ranked.map((c) => oeeDeltaValue(c, baseline) ?? 0));

  return (
    <Card data-testid="candidate-ranking">
      <CardHeader className="flex-row items-baseline justify-between gap-3 space-y-0">
        <CardTitle>Ranking de candidatos</CardTitle>
        <span className="text-[11px] text-text-tertiary">
          dois eixos separados · cada um começa na base
        </span>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* O cabeçalho nomeia cada eixo e diz o que significa cair de cada lado da base: sem isso
            a régua vertical é só um traço. Compartilha a malha das linhas, então aponta sempre
            para a coluna certa. */}
        <div className={cn(grid, "px-2 text-[10px] leading-tight text-text-tertiary")}>
          <span />
          <AxisHead title={axis.title} left={axis.left} right={axis.right} />
          {showOee ? <AxisHead title="Δ OEE" left="menor" right="maior" /> : null}
        </div>

        {/* As réguas são desenhadas AQUI, atrás de todas as linhas, e não dentro de cada faixa:
            um tique de 16px por linha fica escondido atrás da própria barra que deveria
            referenciar. Contínuas, elas viram o eixo que dá sentido a "esquerda" e "direita". */}
        <div className="relative">
          <div className={cn(grid, "pointer-events-none absolute inset-0")} aria-hidden>
            <span />
            <AxisRule />
            {showOee ? <AxisRule /> : null}
          </div>

          <div className="relative divide-y divide-border-subtle">
            {ranked.map((c) => (
              <CandidateRankingRow
                key={c.simulationId}
                batch={batch}
                candidate={c}
                baseline={baseline}
                axis={axis}
                showOee={showOee}
                gridClass={grid}
                objDomain={objDomain}
                oeeDomain={oeeDomain}
                onOpen={onOpen}
              />
            ))}
            {failed.map((c) => (
              <CandidateRankingFailedRow
                key={c.simulationId}
                candidate={c}
                showOee={showOee}
                gridClass={grid}
              />
            ))}
          </div>
        </div>

        <div className={cn(grid, "px-2 text-[10px] text-text-tertiary")}>
          <span />
          <span className="text-center">base</span>
          {showOee ? <span className="text-center">base</span> : null}
        </div>

        <RankingLegend hasFailed={failed.length > 0} />
        <RankingOeeNote ranked={ranked} axis={axis} showOee={showOee} />

        <details className="group border-t border-border-subtle pt-3">
          <summary className="flex cursor-pointer list-none items-center gap-1 text-[11px] text-text-secondary hover:text-text-primary [&::-webkit-details-marker]:hidden">
            <ChevronRight className="h-3.5 w-3.5 transition-transform group-open:rotate-90" />
            Ver como tabela
          </summary>
          <div className="overflow-x-auto pt-2">
            <CandidateRankingTable
              batch={batch}
              baseline={baseline}
              ranked={ranked}
              failed={failed}
              axis={axis}
              showOee={showOee}
            />
          </div>
        </details>
      </CardContent>
    </Card>
  );
}

function AxisHead({ title, left, right }: { title: string; left: string; right: string }) {
  return (
    <span className="text-center">
      <span className="block text-[11px] text-text-secondary">{title}</span>
      <span className="block">
        ← {left} · {right} →
      </span>
    </span>
  );
}

/** A régua do zero. Largura ZERO + borda de 1px: é o que a faz cair no mesmo pixel do espaçador
 * de zero do `DeviationTrack`, em vez de 1px ao lado dele. */
function AxisRule() {
  return (
    <span className="flex">
      <span className="flex-1" />
      <span className="border-l border-dashed border-border-default" />
      <span className="flex-1" />
    </span>
  );
}
